import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";

type PanelLink = { href: string; label: string; group: string };

/**
 * Grouped "further reading" panel shown under an article's FAQ.
 * English panels navigate with the router; Bangla panels use plain anchors.
 */
export function ArticleLinkPanel({
  links,
  groupOrder,
  eyebrow,
  title,
  className,
  router,
}: {
  links: PanelLink[];
  groupOrder: readonly string[];
  eyebrow: string;
  title: string;
  className: string;
  router: boolean;
}) {
  if (links.length === 0) return null;
  const grouped = groupOrder
    .map((g) => ({ group: g, items: links.filter((l) => l.group === g) }))
    .filter((x) => x.items.length > 0);
  const linkClass =
    "group inline-flex items-start gap-2 text-sm text-foreground hover:text-primary";
  const content = (label: string) => (
    <>
      <ArrowUpRight size={14} className="mt-1 text-primary shrink-0" />
      <span className="underline-offset-4 group-hover:underline">{label}</span>
    </>
  );

  return (
    <div className={className}>
      <div className="text-xs uppercase tracking-[0.2em] text-primary">{eyebrow}</div>
      <h3 className="mt-3 font-display text-2xl">{title}</h3>
      <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-8">
        {grouped.map(({ group, items }) => (
          <div key={group}>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
              {group}
            </div>
            <ul className="space-y-2">
              {items.map((it) => (
                <li key={it.href}>
                  {router ? (
                    <Link to={it.href as never} className={linkClass}>
                      {content(it.label)}
                    </Link>
                  ) : (
                    <a href={it.href} className={linkClass}>
                      {content(it.label)}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
